import type { HeaterType, ProductRecommendation, ProductRecommendationsResult } from '../../engines/types';
import { PRODUCT_RECOMMENDATIONS } from '../../data/productRecommendations';

export interface FilterProductsInput {
  heaterType: HeaterType;
  recommendedKw: number;
  /** The catalogue to pick from; the shipped one unless a test supplies its own. */
  catalogue?: readonly ProductRecommendation[];
}

/** Reserved hosts (RFC 2606/6761) that only ever mark a placeholder link. */
const PLACEHOLDER_HOSTS = /(^|\.)(example\.(com|net|org)|test|invalid|localhost)$/i;

/**
 * A product is only recommended once it points at a real shop. A catalogue
 * entry with a placeholder link carries an invented price and a stand-in
 * image; showing it would quote a figure nobody can buy at.
 */
export function isPurchasableProduct(item: ProductRecommendation): boolean {
  try {
    const { protocol, hostname } = new URL(item.affiliateUrl);
    return protocol === 'https:' && !PLACEHOLDER_HOSTS.test(hostname);
  } catch {
    return false;
  }
}

export function filterProducts({
  heaterType,
  recommendedKw,
  catalogue = PRODUCT_RECOMMENDATIONS,
}: FilterProductsInput): ProductRecommendationsResult {
  const matchingItems = catalogue.filter((item) => {
    if (!isPurchasableProduct(item)) return false;
    if (!item.heaterTypes.includes(heaterType)) return false;
    if (!item.kwRange) return true;
    return recommendedKw >= item.kwRange[0] && recommendedKw <= item.kwRange[1];
  });

  const heaterItems = matchingItems.filter((item) => item.category === 'heater').slice(0, 3);
  const accessoryItems = matchingItems.filter((item) => item.category !== 'heater');

  const items = [
    ...heaterItems,
    ...accessoryItems.filter((item) => item.category === 'chimney').slice(0, heaterType === 'wood' ? 1 : 0),
    ...accessoryItems.filter((item) => item.category === 'door').slice(0, 1),
    ...accessoryItems.filter((item) => item.category === 'lighting').slice(0, 1),
  ];

  return { items };
}
